import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { includes, keys, some, first, difference } from 'lodash';
import core from '@gin-betting/core';
import banners, { BannerTypes } from '@gin-betting/banners';
import responsiveComponents from '@gin-betting/responsive-components';

import { useMemoSelector, usePureSelector, usePrevious } from 'hooks';
import { ACTIVE_MARKET_STATUSES } from 'app-constants';
import { getMissingMatches } from '../../../actions';
import type { ZoneLocation } from '../../../types';
import { makeSelectZones } from '../../../selectors';
import { ALL, NUMBER_OF_ROWS } from '../../../constants';

const {
    entities: {
        selectors: { selectMissingSportEventIds },
    },
    utils: { getBettingOptions },
    hooks: { useDeepMemo },
} = core;

const {
    actions: { getBannersData },
    selectors: { makeSelectBannerZonesByAttributes },
} = banners;

const { useResponsiveContainer } = responsiveComponents;

interface Input {
    zoneAttributes: BannerTypes.BannerZoneAttributes[];
}

interface Output {
    activeZoneLocation: ZoneLocation | undefined;
    activeSport: string;
    zoneLocations: ZoneLocation[];
    sportEventIds: string[];
    sportIds: string[];
    countRenderMarkets: number;
    isShowTopMatch: boolean;
    isNarrow: boolean;
    makeHandleChangeZoneLocation: (zoneLocation: ZoneLocation) => VoidFunction;
    makeHandleChangeSport: (sportId: string) => VoidFunction;
    containerRef: (node: Element | null) => void;
}

const requestedZonesCache: string[] = [];

const useTopMatch = ({ zoneAttributes }: Input): Output => {
    const dispatch = useDispatch();
    const { containerRef, currentBreakpoints } = useResponsiveContainer({
        breakpoints: { small: 380, medium: 480, big: 650 },
    });

    // TODO: Calculate market count based on zone availability instead of element width
    const countRenderMarkets =
        currentBreakpoints.length === 0
            ? 0
            : includes(currentBreakpoints, 'big')
            ? 3
            : 1;

    const [activeZoneId, setActiveZoneId] = useState<string>();
    const [activeSport, setActiveSport] = useState<string>(ALL);

    const bannerZones = useMemoSelector(
        // NOTE: Currently "zoneAttributes" does not change, no need to re-create the selector fn
        makeSelectBannerZonesByAttributes(zoneAttributes),
        // TODO: Delete this obj whe "selectSportTypeFromLocation" is replaced
        {}
    );

    const memBannerZones = useDeepMemo(bannerZones);

    const memBannerZoneIds = useDeepMemo(memBannerZones.map(z => z.id));

    const selectZones = useMemo(() => makeSelectZones(memBannerZoneIds), [
        memBannerZoneIds,
    ]);

    const zones = usePureSelector(selectZones);

    const zonesWithEvent = memBannerZones.filter(({ id }) =>
        some(zones[id], sportIdsSet => !!sportIdsSet.size)
    );

    const memZoneWithEventIds = useDeepMemo(
        () => zonesWithEvent.map(({ id }) => id),
        [zonesWithEvent]
    );

    const activeZoneLocation = memBannerZones.find(z => z.id === activeZoneId)
        ?.attributes.pageLocation as ZoneLocation | undefined;

    const zone = activeZoneId ? zones[activeZoneId] : undefined;

    const sportEventIds: string[] = Array.from(zone?.[activeSport] ?? []);

    const missingIds = useMemoSelector(selectMissingSportEventIds, {
        sportEventIds,
    });

    const memMissingSportEventIds = useDeepMemo(missingIds);

    const isShowTopMatch = some(
        zones,
        item => (item?.[ALL]?.size ?? 0) >= NUMBER_OF_ROWS
    );

    useEffect(() => {
        if (isShowTopMatch) {
            document.documentElement.style.setProperty(
                '--weekly-widget-height',
                `320px`
            );
        }
    }, [isShowTopMatch]);

    const makeHandleChangeZoneLocation = useCallback(
        (zoneLocation: ZoneLocation) => () => {
            const zoneId = memBannerZones.find(
                z => z.attributes.pageLocation === zoneLocation
            )?.id;

            if (!zoneId) return;

            setActiveZoneId(zoneId);
            setActiveSport(ALL);
        },
        [memBannerZones]
    );

    const makeHandleChangeSport = useCallback(
        (sportId: string) => () => {
            setActiveSport(sportId);
        },
        []
    );

    const prevCountRenderMarkets = usePrevious(countRenderMarkets);

    useEffect(() => {
        if (
            memMissingSportEventIds.length === 0 ||
            // NOTE: Prev value for count is required to avoid obsolete requests
            //       when widget is rendered empty at first
            prevCountRenderMarkets === undefined ||
            countRenderMarkets === 0 ||
            countRenderMarkets < prevCountRenderMarkets
        ) {
            return;
        }

        dispatch(
            getMissingMatches({
                sportEventsIds: memMissingSportEventIds,
                isTopMarkets: true,
                marketLimit: countRenderMarkets,
                withMarketsCount: true,
                marketStatuses: ACTIVE_MARKET_STATUSES,
            })
        );
        /* "prevCountRenderMarkets" is mutable ref.current */
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [dispatch, countRenderMarkets, memMissingSportEventIds]);

    useEffect(() => {
        const firstZoneId = first(memZoneWithEventIds);

        if (!firstZoneId) return;

        setActiveZoneId(firstZoneId);
    }, [memZoneWithEventIds]);

    useEffect(() => {
        memBannerZoneIds.forEach(zoneId => {
            if (requestedZonesCache.includes(zoneId)) return;

            requestedZonesCache.push(zoneId);

            dispatch(
                getBannersData({
                    zoneId,
                    marketStatuses: ACTIVE_MARKET_STATUSES,
                    withSportEvent: false,
                    sportIds: getBettingOptions().sportIds,
                })
            );
        });
    }, [dispatch, memBannerZoneIds]);

    return {
        activeZoneLocation,
        activeSport,
        countRenderMarkets,
        isShowTopMatch,
        zoneLocations: zonesWithEvent.map(
            z => z.attributes.pageLocation as ZoneLocation
        ),
        sportIds: keys(zone),
        sportEventIds: difference(sportEventIds, memMissingSportEventIds).slice(
            0,
            NUMBER_OF_ROWS
        ),
        isNarrow:
            currentBreakpoints.length === 1 &&
            currentBreakpoints[0] === 'small',
        makeHandleChangeZoneLocation,
        makeHandleChangeSport,
        containerRef,
    };
};

export default useTopMatch;
